import { IAdminUsersApi, TAdminUsersApiResponse } from "@api/admin.int";
import { IUserPub } from "@api/auth.int";
import { Box, Flex, Icon, IconButton, Input, InputGroup, InputLeftElement, InputRightAddon, Text, Tooltip } from "@chakra-ui/react";
import { AffairFilter, useAffairFilter } from "components/AffairFilter";
import { API_BASE, useDeepAuth, useVVApi } from "components/api";
import { highlight, HitBogyo, HitBox, HitLineMain, HitLineSub, HitRight, HL, PaginatorBox, usePaginator } from "components/paginator";
import { useVVStore } from "components/store";
import { useUrlQuerySearch } from "components/urlSearch";
import { formatDateTime, removeHungarianAccents, splitToWords, toSqlDate } from "components/utils";
import { VizvCard, VizvCardHeaderPopup, VizvMainCol } from "components/vizvComponents";
import { useVVNav } from "components/vvNav";
import { useMemo } from "react";
import { MdFileDownload, MdSearch, MdTune } from "react-icons/md";
import { Outlet, useLocation } from "react-router";
import { useShallow } from 'zustand/react/shallow';




export function AdminUsers() {
    const auth = useDeepAuth();
    const vvNav = useVVNav();
    const api = useVVApi();
    const location = useLocation();
    const store = useVVStore(
        useShallow((state) => ({
            itemsList: state.itemsList,
            setSuggestionList: state.setSuggestionList,
            suggestionList: state.suggestionList,
            isMobile: state.isMobile(),
        })),
    )
    const isDetailed = !vvNav.isRoot();

    const { q, inputQ, setInputQ } = useUrlQuerySearch();
    const qWords = splitToWords(q).map(w => removeHungarianAccents(w));
    const filter = useAffairFilter();

    const pager = usePaginator({
        parse: (res: TAdminUsersApiResponse) => ({ items: res.userList, nextKeys: res.nextKeys }),
        loadNext: async (nextKeys) => {
            return await api.fetch<IAdminUsersApi>({
                url: '/admin/user', method: 'GET', query: {
                    q,
                    fromDate: filter.fromDate ? toSqlDate(filter.fromDate) : undefined,
                    toDate: filter.toDate ? toSqlDate(filter.toDate) : undefined,
                    ...nextKeys && { fromKeys: nextKeys },
                }
            });
        }
        , deps: [q, filter.fromDate, filter.toDate]
    });

    const mapNavRoot = useMemo(() => vvNav.buildRoot('/admin/user', {
        q,
        fromDate: filter.fromDate && toSqlDate(filter.fromDate),
        toDate: filter.toDate && toSqlDate(filter.toDate),
    }), [q, filter.fromDate, filter.toDate]);


    const downloadUrl = new URL('/admin/user/download', API_BASE);

    if (q)
        downloadUrl.searchParams.append('q', q);
    if (filter.fromDate)
        downloadUrl.searchParams.append('fromDate', toSqlDate(filter.fromDate));
    if (filter.toDate)
        downloadUrl.searchParams.append('toDate', toSqlDate(filter.toDate));
    downloadUrl.searchParams.append('x-deep-token', auth.getToken())


    return <>
        <VizvMainCol isTool={{ width: 480, isDetailed }} mapNavRoot={mapNavRoot} >
            <VizvCard isTool
                //{...navBack && { onBack: () => nav(navBack) }}
                isClosable
                mr={5} mt={5}
                maxHeight={store.isMobile ? undefined : "calc(100vh - 4em)"}
            >
                <VizvCardHeaderPopup fontSize='x-large' fontWeight='400' variant="blue" mb={2} alignItems='baseline'  >
                    {/*<Icon color='#0008' as={MdSettings} fontSize='sm' />*/}
                    <Text as='span' color='#0008' fontSize='lg' >admin&nbsp;/&nbsp;</Text>
                    Felhasználók
                </VizvCardHeaderPopup>
                <Box p={2}>
                    <Flex dir="row" >
                        <InputGroup>
                            <InputLeftElement><Icon as={MdSearch} /></InputLeftElement>
                            <Input
                                //ref={inputRef} 
                                value={inputQ}
                                onChange={e => setInputQ(e.target.value)}
                                placeholder="Keresett kifejezés..." />

                            <Tooltip label="Dátum szűrés">
                                <InputRightAddon px={0}><IconButton aria-label="szűrő" colorScheme={filter.isOpen || filter.isAny ? 'secondary' : undefined} icon={<MdTune />} onClick={filter.toggle} /></InputRightAddon>
                            </Tooltip>

                        </InputGroup>
                        <Tooltip label="Excel letöltése">
                            <IconButton aria-label="letöltés" icon={<MdFileDownload />} as='a' target='_blank' href={downloadUrl.toString()} ml={1} />
                        </Tooltip>
                    </Flex>
                </Box>
                <AffairFilter filter={filter} />

                <PaginatorBox pager={pager} item={user =>
                    <HitBox hit={{ user }} highlight={qWords}>
                        <HitBogyo />
                        <HitRight>
                            <HitLineSub><HL text={user.userName} />&nbsp;/&nbsp;{formatDateTime(user.createdAt)}</HitLineSub>
                            <HitLineMain><HL text={user.guard?.name || user.userName} /></HitLineMain>
                            {user.guard?.locationTitle && <HitLineSub>{user.guard?.locationTitle}</HitLineSub>}
                        </HitRight>
                    </HitBox>
                } />

            </VizvCard>
        </VizvMainCol>
        <Outlet />
    </>


}


